import { useRouter } from 'next/router'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getAdvAccount } from '~/api/AdvAccountRepository'
import { getCurrentUserAdv } from '~/api/UserAdvRepository'
import { getPfToken } from '~/api/AuthToken'
import { AdvAccount } from '~/lib/types/AdvAccount'
import { UserAdv } from '~/lib/types/UserAdv'

import { setUserId } from '~/lib/gtmHelper'
import { snackActions } from '~/lib/snackbarHelper'
import { auth, FirebaseUser } from '~/lib/firebase/auth'
import { InflatingAppLoader } from '~/components/common/InflatingAppLoader'
import { isMarketingApiError } from '~/lib/types/error/MarketingApiError'
import { pagesPath } from '~/lib/$path'
import { setClarityCustomTags } from '~/lib/clarirtyHelper'
import { proxyLogin } from '~/lib/proxyLogin'
import { logger } from '~/lib/logger'
import { datadogRum } from '@datadog/browser-rum'
import Bugsnag from '@bugsnag/js'
import { EXTERNAL_LINKS } from '~/lib/externalLinks'
import { isAxiosError } from 'axios'
import { getEventClient } from '~/lib/event'

type IAuth = {
  // 初期値の場合(FirebaseUserが存在するかわからない時)はundefined, FirebaseUserの取得を試み、失敗した(存在しない)場合はnull
  currentUser: FirebaseUser | null | undefined
  currentUserAdv: UserAdv | null | undefined
  setCurrentUserAdv: (user: UserAdv) => void
  currentAdv: AdvAccount | null | undefined
  setCurrentAdv: (account: AdvAccount | null) => void
  /**
   * AdvAccountが参照している外部システムの状態が更新されて、状態の整合性を保ちたい場合などに
   * AdvAccountを再取得する（例：クレジットカードの登録）
   */
  refetchCurrentAdv: () => Promise<{ id: number } | undefined>
}

const AuthContext = createContext<IAuth>(undefined as never)

export const AuthContextProvider = AuthContext.Provider

export const useAuth = () => {
  const { currentUser, currentAdv, currentUserAdv, ...rest } = useContext(AuthContext)

  if (currentUser && currentUserAdv && currentAdv) {
    return { currentUser, currentAdv, currentUserAdv, ...rest }
  }

  throw new Error('Invalid useAuth call')
}

export const usePartialAuth = () => {
  const { currentUser, currentAdv, currentUserAdv, ...rest } = useContext(AuthContext)

  if (currentUser !== undefined && currentUserAdv !== undefined && currentAdv !== undefined) {
    return { currentUser, currentAdv, currentUserAdv, ...rest }
  }

  throw new Error('Invalid usePartialAuth call')
}

export const useAuthController = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { currentUser, currentAdv, currentUserAdv, ...rest } = useContext(AuthContext)

  return rest
}

export const excludePathes = [
  // signup, signinのパス配下ではチェックしない．このケースのみ認証状態に基づくredirect処理は各ページで行う
  pagesPath.reset_password.$url().pathname,
  pagesPath.signin.$url().pathname,
  pagesPath.signup.$url().pathname,
  pagesPath.notification_email.verify._verification_uuid('').$url().pathname.split('/verify')[0] ??
    '/notification_email',
  // API設定画面は認証不要
  '/dev/api-config',
  // パスワードリセットメール内リンクから直接アクセスされるため認証チェック対象外
  pagesPath.auth.action.$url().pathname,
]

export const AuthProvider = (props: { children: React.ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>()
  const [currentUserAdv, setCurrentUserAdv] = useState<UserAdv | null>()
  const [currentAdv, setCurrentAdv] = useState<AdvAccount | null>()
  const router = useRouter()

  // FIXME: ページ遷移（URLのパス変更）のたびにwindow.dataLayerに新しいconfigが追加され、
  //        setUserIdした値が書き換えられてします
  //        暫定対応：router.pathnameが変化するたびにsetしなおす
  //        そもそも何経由でこういう挙動になっているのかの調査は必要そう
  useEffect(() => {
    if (currentUserAdv?.id) {
      setUserId(currentUserAdv.id)
    }
  }, [router.pathname])

  const refetchCurrentAdv = async () => {
    try {
      const advAccount = await getAdvAccount()
      setCurrentAdv(advAccount)
      return { id: advAccount.id }
    } catch (_) {
      snackActions.error('アカウント情報の更新に失敗しました')
    }
  }

  const isAuthPage = useMemo(() => excludePathes.some((path) => router.pathname.startsWith(path)), [router.pathname])

  const redirectToSignIn = () => {
    if (isAuthPage) return // 認証が不要なページであればリダイレクトしない

    if (router.pathname === '/') router.push(EXTERNAL_LINKS.TOP_MARKETING)
    else router.push(pagesPath.signin.$url())
  }

  const handleAuthenticationError = (e: unknown) => {
    // TODO: エラーが記録されてなさそう
    if (!isMarketingApiError(e)) {
      snackActions.warning('エラーが発生しました')
      router.push(pagesPath.signin.$url())
      return
    }
    // FirebaseUserが存在すれば、userAdv, advAccountの作成画面へ遷移
    if (e.response.status === 401) return redirectToSetUpAccount()
    if (e.response.status === 400 && e.response.data.error.code === 'E1004') {
      console.log(e.response.data)
      redirectToSetUpAccount()
    }
  }

  const redirectToSetUpAccount = () => {
    snackActions.warning('サインアップ作業が途中です')
    if (router.pathname !== pagesPath.signup.account.$url().pathname) {
      router.push(pagesPath.signup.account.$url())
    }
  }

  // userAdvとadvAccountを取得してstateにセット
  const setupAccounts = async () => {
    let isSupport = false
    let userAdv: UserAdv | null = null
    let advAccount: AdvAccount | null = null

    // 最初に/authエンドポイントを呼び出す
    try {
      await getPfToken()
    } catch (e) {
      // 401エラーの場合、/signup/accountにリダイレクト
      setCurrentUserAdv(null)
      setCurrentAdv(null)
      handleAuthenticationError(e)
      // 401エラーの場合は正常動作のためログを送信しない
      if (!isAxiosError(e) || e.response?.status !== 401) {
        logger.error(new Error('Failed to authenticate', { cause: e }))
      }
      return
    }

    // /authが成功した場合、既存の処理を続行
    try {
      userAdv = await getCurrentUserAdv() // userAdvの取得
      isSupport = userAdv.authority === 'support'

      // パスワードリセット強制チェック
      // 条件: passwordResetCompletedAt が null AND パスワード認証ユーザー AND support以外
      // パスワードリセット関連ページにいる場合はスキップ（リセット操作中の再リダイレクトを防ぐ）
      const isPasswordResetPage =
        router.pathname === pagesPath.reset_password.$url().pathname ||
        router.pathname === pagesPath.auth.action.$url().pathname
      if (
        userAdv.passwordResetCompletedAt === null &&
        currentUser?.providerData.some((p) => p.providerId === 'password') &&
        userAdv.authority !== 'support' &&
        !isPasswordResetPage
      ) {
        // canRender() で認証ページ（/reset_password, /signin, /signup）を表示可能にするため、
        // state を null にクリアしてからリダイレクトする。
        // これにより他の UI コンポーネントが一瞬 null を参照する可能性があるが、
        // 即座にリダイレクトするため実質的な影響はない。
        setCurrentUserAdv(null)
        setCurrentAdv(null)
        if (currentUser.email) {
          try {
            await auth.sendPasswordResetEmail(currentUser.email)
          } catch (e) {
            // メール送信失敗時もリダイレクトは継続する。
            // ユーザーは /reset_password?forced=true の「メールを再送信する」ボタンで再送可能。
            logger.error(new Error('パスワードリセットメールの送信に失敗しました', { cause: e }))
          }
        } else {
          logger.error(new Error('パスワードリセット対象ユーザーの email が取得できませんでした'))
        }
        router.push({ pathname: pagesPath.reset_password.$url().pathname, query: { forced: 'true' } })
        return
      }

      setCurrentUserAdv(userAdv)
    } catch (e) {
      setCurrentUserAdv(null)
      setCurrentAdv(null) // userAdvの取得に失敗した場合は、advAccountも存在しないことが決まるため、advAccountの取得は行わずにnullにする
      handleAuthenticationError(e)
      // 以下の場合は401エラーになるが、正常動作のためログを送信しない(/singup/accountにリダイレクトされる)
      // - FirebaseUser作成(パスワード登録)直後の1回
      // - FirebaseUser作成後、会社情報登録から離脱した状態でログインした
      if (!isAxiosError(e) || e.response?.status !== 401) {
        logger.error(new Error('Failed to fetch user adv', { cause: e }))
      }
      return
    }
    // userAdvのが正常に取得できた場合のみ、advAccountの取得を開始する。
    // userAdvのみ存在し、advAccountがない場合は、サーバーやアプリ側で起こりうるため。
    // 逆にuserAdvはないが、advAccountが存在することはありえないため、userAdvの結果に応じてadvAccountの取得をする
    try {
      advAccount = await getAdvAccount()
      if (isSupport) {
        // 代理ログインの場合は対象のAdvAccountのIDをセット
        proxyLogin.setProxyAdvAccountId(advAccount.id)
      }
      setCurrentAdv(advAccount)

      // イベントクライアントにアカウントコンテキストをセット
      if (userAdv && advAccount && userAdv.id !== null) {
        getEventClient().setAccountContext({
          adv_account_id: advAccount.id,
          user_adv_id: userAdv.id,
        })
      }
    } catch (e) {
      setCurrentAdv(null)
      handleAuthenticationError(e)
      logger.error(new Error('Failed to fetch adv account', { cause: e }))
    }
  }

  useEffect(() => {
    // FirebaseUserが存在していればそれをセットし、存在しなければ初期値のundefinedからnullに更新
    auth.onAuthStateChanged(async (user: FirebaseUser | null) => setCurrentUser(user))
  }, [])

  useEffect(() => {
    if (currentUser === undefined) return // FirebaseUser(currentUser)が初期値の場合(FirebaseUserの取得を試みていない場合)は何もしない

    if (currentUser === null) {
      // currentUserの取得を試みた結果、ユーザー存在しなかった場合(未ログイン or そもそもアカウントを作成していない場合)は、サインインページへリダイレクト
      setCurrentUserAdv(null)
      setCurrentAdv(null)
      // イベントクライアントのアカウントコンテキストをクリア
      getEventClient().clearAccountContext()
      redirectToSignIn()
    } else {
      // FirebaseUserが存在する場合は、userAdvとadvAccountを順番に取得し、stateにセットする
      setupAccounts()
    }
  }, [currentUser])

  // 全てのアカウント情報の取得が成功している場合に、
  // 全ての環境でDatadog RUM、GTM、Bugsnag、Clarityへユーザー情報を設定する
  useEffect(() => {
    if (!currentUser || !currentUserAdv || !currentAdv) return

    datadogRum.setUser({
      id: currentAdv.id.toString(),
      userAdvId: currentUserAdv.id?.toString(),
    })

    setUserId(currentUserAdv.id)

    Bugsnag.setUser(`${currentUserAdv.id}`, currentUserAdv.email, currentUserAdv.name)

    setClarityCustomTags('user_adv_id', currentUserAdv.id?.toString() || 'anonymous')
    setClarityCustomTags('user_name', currentUserAdv.name || 'anonymous')
    setClarityCustomTags('adv_account_id', currentAdv.id.toString() || 'anonymous')
    setClarityCustomTags('company_name', currentAdv.companyName || 'anonymous')
  }, [currentUser, currentUserAdv, currentAdv])

  const canRender = () => {
    // 全てのアカウント情報が揃っていたら表示可能
    if (currentUser && currentUserAdv && currentAdv) return true

    // 認証ページであれば、読み込みがすべて完了すれば表示可能
    if (isAuthPage && currentUser !== undefined && currentUserAdv !== undefined && currentAdv !== undefined) {
      // ただしログイン後にadvAccount, userAdvの取得に失敗したときは、/signup/account以外を表示しない
      // redirectToSetUpAccountと対応する
      if (
        currentUser !== null &&
        (currentUserAdv === null || currentAdv === null) &&
        router.pathname !== pagesPath.signup.account.$url().pathname &&
        router.pathname !== pagesPath.reset_password.$url().pathname
      ) {
        return false
      }

      return true
    }

    return false
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        currentUserAdv,
        setCurrentUserAdv,
        currentAdv,
        setCurrentAdv,
        refetchCurrentAdv,
      }}
    >
      {canRender() ? props.children : <InflatingAppLoader />}
    </AuthContext.Provider>
  )
}
